import React, { useEffect,useState } from 'react'
import * as joda from '@js-joda/core'
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom'
import { Avatar } from 'primereact/avatar'
import { Badge } from 'primereact/badge'
import { Conversation } from 'types/messages'
import useSignalR,{ mkMethod } from '../../../_core/hooks/useSignalR'
import { HubInvoke, HubReceive } from 'enums/HubMethods'
import { authRoutes,webNav,mobileNav } from '../../../_core/router/routes'
import BreakpointRender from '../../../_core/components/breakpoint-render'
import { ContextInteractions,Interactions,Like,Match,SeenState,mapSeen } from '../../../_core/contexts/interactions'
import { ContextCurrentUser } from '../../../_core/contexts/currentUser'
import Await from '../../../_core/components/await'
import { useAuth } from '../../../_core/hooks/useAuth'
import { getCurrentUser } from '_services/users.service'
import { useFetchV2 } from '../../../_core/hooks'
import { EnumOptionsContextType,ContextEnumOptions } from '../../../_core/contexts/enumOptions'
import { nullPromise, zeroInstantString } from 'prelude'

import { getBodyTypesEnum, getCountryOptions, getNationalitiesEnum, getPoliticalBeliefsEnum, getReligionsEnum, getUserInterestsEnum } from '_services/enums.service'
import {BANNER_MESSAGE, BANNER_STATUS} from '@env'
import NewsBanner from '../../../components/newsBanner'

let intervalMatchesMessagesLikes : number | undefined = undefined

	
let bannerObserverAttached = false

const AuthLayout = () => {
  const [auth] = useAuth()
  const [currentUser,reloadCurrentUser] = useFetchV2(getCurrentUser,undefined,[])
  const location = useLocation()
  const params = useParams()
  const currentRoute = authRoutes.find(r => `/${r.replaced ? r.replaced(params) : r.path}` === location.pathname)

  const [convos, setConvos] = useState<Promise<Conversation[]>>(nullPromise)
  const [likes, setLikes] = useState<Promise<Like<joda.Instant>[]>>(nullPromise)
  const [matches, setMatches] = useState<Promise<Match<joda.Instant>[]>>(nullPromise)
  const [unread, setUnread] = useState<Promise<{ [_:string]:number }>>(nullPromise)
  const [seen, setSeen] = useState<Promise<SeenState<joda.Instant>>>(nullPromise)
  const [invoke,wsstatus] = useSignalR([
    mkMethod([HubReceive.Likes,(xs:Like<string>[]) =>
      setLikes(Promise.resolve(xs.map((x) => ({ ...x,'occurred':joda.Instant.parse(x.occurred ?? zeroInstantString) }))))
    ]),
    mkMethod([HubReceive.Conversations,(x:Conversation[]) => setConvos(Promise.resolve(x))]),
    mkMethod([HubReceive.Matches,(xs:Match<string>[]) =>
      setMatches(Promise.resolve(xs.map((x) => ({ ...x,'occurred':joda.Instant.parse(x.occurred ?? zeroInstantString) }))))
    ]),
    mkMethod([HubReceive.Unread,(x:{ [_:string]:number }) => setUnread(Promise.resolve(x))]),
    mkMethod([HubReceive.Seen,(x:SeenState<string>) => setSeen(Promise.resolve(mapSeen(x)))]),
  ])
  // TODO: need to apply ignore pattern like `useFetch`?
  const fetchMatchesMessagesLikes = async () => {
    setLikes(invoke(HubInvoke.GetLikes,[],(xs:Like<string>[]) =>
      xs.map((x) => ({ ...x,'occurred':joda.Instant.parse(x.occurred ?? zeroInstantString) }))
    ))
    setConvos(invoke(HubInvoke.GetConversations,[],(xs:Conversation[]) => xs))
    setMatches(invoke(HubInvoke.GetMatches,[],(xs:Match<string>[]) =>
      xs.map((x) => ({ ...x,'occurred':joda.Instant.parse(x.occurred ?? zeroInstantString) }))
    ))
    setUnread(invoke(HubInvoke.GetUnread,[],(xs:{ [_:string]:number }) =>
      xs
    ))
    setSeen(invoke(HubInvoke.GetSeen,[],mapSeen))
  }
  useEffect(() => {
    fetchMatchesMessagesLikes()
    intervalMatchesMessagesLikes = window.setInterval(() => {
      fetchMatchesMessagesLikes()
    },60 * 60 * 1000)
    return () => window.clearInterval(intervalMatchesMessagesLikes)
  },[])

  const [enumDataRaw] = useFetchV2<EnumOptionsContextType,undefined>(async () => {
    try
    {
      const [countries, nationalities, bodyTypes, religions, politicalBeliefs, userInterests] = await Promise.all([
        getCountryOptions(),
        getNationalitiesEnum(),
        getBodyTypesEnum(),
        getReligionsEnum(),
        getPoliticalBeliefsEnum(),
        getUserInterestsEnum(),
      ])
      return {
        'countryData':countries.map((option) => ({
          id: String(option.gid),
          display: option.nameLong,
          advancedOption: !!option.name, // TODO - what did this mean?: option.name,
        })),
        'nationalityData':nationalities.map((option) => ({
          id: option.isoCode,
          display: option.nationality,
          advancedOption: option.advancedOption,
        })),
        'bodyTypeData':bodyTypes.map((option) => ({
          id: option.name,
          display: option.display,
          advancedOption: false,
        })),
        'religionData':religions.map((option) => ({
          id: option.name,
          display: option.display,
          advancedOption: option.advancedOption,
        })),
        'politicalBeliefData':politicalBeliefs.map((option) => ({
          id: option.name,
          display: option.display,
          advancedOption: false,
        })),
        'userInterestsData':userInterests.map((option) => ({
          id: option.name,
          display: option.display,
          advancedOption: false,
          'category':option.category,
        })).sort((a, b) => a.display.localeCompare(b.display)),
        'loading':false,
        'error':null,
      }
    }
    catch(e)
    {
      return {
        'countryData':[],
        'nationalityData':[],
        'bodyTypeData':[],
        'religionData':[],
        'politicalBeliefData':[],
        'userInterestsData':[],
        'loading':false,
        'error':`Failed to fetch data: ${e}`,
      }
    }
  },undefined,[])
  const [enumData,setEnumData] = useState<EnumOptionsContextType>()
  React.useEffect(() => {
    (async () => {
      setEnumData(await enumDataRaw)
    })()
  },[enumDataRaw])

  const interactions : Interactions = React.useMemo(() => ({
    'likes':likes,
    'matches':matches,
    'conversations':convos,
    'unread':unread,
    'seen':seen,
    'setSeen':(x) => setSeen(Promise.resolve(mapSeen(x))),
  }),[likes,matches,convos,unread,seen])

  return <>
    <ContextCurrentUser.Provider value={[currentUser,() => reloadCurrentUser(undefined)]}>
    <ContextInteractions.Provider value={interactions}>
    <ContextEnumOptions.Provider value={enumData ?? {
      'countryData':[],
      'nationalityData':[],
      'bodyTypeData':[],
      'religionData':[],
      'politicalBeliefData':[],
      'userInterestsData':[],
      'loading':true,
      'error':null,
    }}>
    <div className="template-auth-root h-100 d-flex flex-column">
      <NewsBanner
        message={BANNER_MESSAGE}
        isDisplayed={BANNER_STATUS}
      />
      {/* mobile header */}
      <BreakpointRender eq="mobile">
      <header className="d-flex p-2">
        <NavLink className="nav-link" to="/discover">
          <img
            src="/assets/images/horizontal-logo.png"
            srcSet="/assets/images/horizontal-logo@2x.png 2x"
            width="350px" className="thelogo"
            alt="Dating Across the Pond"
          />
        </NavLink>
      </header>
      </BreakpointRender>
      {/* web header */}
      <BreakpointRender eq="desktop">
      <header className="p-2">
        <div className="card shadow w-100">
          <div className="card-body p-1">
            <div className="d-flex justify-content-between align-items-center w-100">
              <NavLink
                onClick={e => {
                  if (!currentRoute?.renderNav) e.preventDefault();
                }}
                className="nav-link px-3"
                to="/discover"
                aria-disabled={!currentRoute?.renderNav}
              >
                <img
                  src="/assets/images/horizontal-logo.png"
                  srcSet="/assets/images/horizontal-logo@2x.png 2x"
                  width="350px"
                  alt="Dating Across the Pond"
                />
              </NavLink>
              <ul className="navbar-nav flex-row align-items-center d-flex">
                {webNav(auth)
                  .filter(_ => {
                    return currentRoute?.renderNav
                  })
                  .map((route) => (
                    <li className="nav-item" key={route.path}>
                      <NavLink
                        to={`/${route.path}`}
                        className={({ isActive }) =>
                          isActive ? 'btn btn-primary px-4 ms-1' : 'btn px-4 ms-1'
                        }
                      >
                        {route.title}
                      </NavLink>
                    </li>
                  ))}
                <li>
                  <NavLink
                    to={`/profile`}
                    className={({ isActive }) => (isActive ? 'disabled-link nav-link px-4 ' : 'disabled-link nav-link px-4')}
                    onClick={e => {
                      if (!currentRoute?.renderNav) e.preventDefault();
                    }}
                  >
                    <Avatar
                      title="profile"
                      image={auth.img}
                      size="large"
                      shape="circle"
                      // onMouseEnter={(event) => menuAvatarRight.current?.toggle(event)}
                    />
                    {/* <Menu
                      onMouseLeave={(event) => menuAvatarRight.current?.toggle(event)}
                      model={avatarMenus}
                      popup
                      ref={menuAvatarRight}
                      id="popup_menu_left"
                    /> */}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>
      </BreakpointRender>
      <main className="position-relative p-2 flex-grow-1" style={{ 'minHeight':0 }}>
        <Outlet />
      </main>
      <BreakpointRender eq="mobile">
      {!currentRoute?.renderNav ? undefined : <nav className="w-100 justify-content-center p-0">
        <ul className="navbar-nav align-items-center d-flex flex-row justify-content-evenly w-100 h-100">
          {mobileNav(auth)
            .map((route) => (
              <li className="nav-item" key={route.path}>
                <span className="p-overlay-badge">
                  <NavLink
                    onClick={e => {
                      if (!currentRoute?.renderNav) e.preventDefault();
                    }}
                    to={`/${route.path}`}
                    end
                    aria-disabled={!currentRoute?.renderNav}
                    className={({ isActive }) => (isActive ? 'icon-active-color' : 'icon-non-active-color')}
                  >
                    <span className="d-flex justify-content-center">
                      <span className={route.icon} style={{fontSize: '1.5rem'}}></span>
                    </span>
                    {route.title}
                  </NavLink>
                  <Await dependency={route.badge(interactions)}>
                    {(badge) => badge ? <Badge value={badge}></Badge> : undefined}
                  </Await>
                </span>
              </li>
            ))}
        </ul>
      </nav>}
      </BreakpointRender>
    </div>
    </ContextEnumOptions.Provider>
    </ContextInteractions.Provider>
    </ContextCurrentUser.Provider>
    {wsstatus === 'disconnected' && <div className="d-none position-fixed top-0 w-100 bg-danger text-white text-center">Lost the connection to web server. Attempting to reconnect...</div>}
    {wsstatus === 'error' && <div className="d-none position-fixed top-0 w-100 bg-danger text-white text-center">We're unable to establish a connection to the server. Please try reloading.</div>}


<Await dependency={currentUser}>
  {(user) => {
    //console.log('Resolved currentUser:', user)
    console.log('User activePurchases:', user.activePurchases)

    const isMan =
      user.userMetadata?.UserGender?.name === 'man' ||
      user.userMetadata?.sexName === 'man'

    const isNonPremium =
      !user.activePurchases || user.activePurchases.length === 0

    if (isMan && isNonPremium) {
      const injectBanner = () => {
        const header = document.querySelector(
          '.profile-box-width .p-card-header'
        )
		  console.log('Header found:', !!header)
        if (header && !header.querySelector('.non-premium-banner')) {
          header.innerHTML = `
            <div class="non-premium-banner" style="text-align: center; padding:8px;">
              Special offer: upgrade for 50% off! Use coupon code <strong>50SALE</strong> at checkout. 
              <a href="/upgrade" style="color:#495057; font-weight: bold;">Upgrade now</a>
            </div>
          `
          console.log('Banner injected (non-premium male user)')
        }
      }

      // Try injecting immediately
      setTimeout(() => {
        injectBanner()

        if (!bannerObserverAttached) {
          const target = document.querySelector(
            '.profile-box-width .p-card-header.bg-primary-content[data-pc-section="header"]'
          )?.parentElement?.parentElement // adjust as needed to watch surrounding container

          if (target) {
            const observer = new MutationObserver(() => {
              injectBanner()
            })

            observer.observe(target, {
              childList: true,
              subtree: true,
              characterData: true,
            })

            bannerObserverAttached = true
            console.log('Observer attached')
          }
        }
      }, 500)
    }

    return null
  }}
</Await>

	
  </>
}

export default React.memo(AuthLayout)
